/**
 * "Thêm chủ thể" / "Sửa chủ thể": the managing customer (create), the subject's fields — the
 * shared ./SubjectFields.tsx, the same component "Thêm khách hàng" uses for the customer's
 * primary subject — and, when editing, the reason. The server checks everything again and
 * answers `VALIDATION_FAILED` with `details.fields`, shown next to each field; the form's own
 * checks (../../lib/subjectDraft.ts) give the same codes before sending.
 *
 * Editing makes a new version: identity number and birth date left empty keep the values on
 * file (only their masked hints are shown). The form says whether the edit keeps the current
 * verification — an individual's eKYC (name, document and date of birth unchanged), an
 * organisation's subject declaration (declared values unchanged) — or needs a new one
 * (../../lib/subjectNames.ts `verificationNotice`).
 */

import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { api, isApiError } from '../../api';
import type { SubjectFieldError, SubjectInput, SubjectProfile } from '../../api/areas/accounts';
import { Loading } from '../../components/States';
import { useWrite, WriteError } from '../../components/Write';
import { blankSubject, subjectErrors, subjectFromProfile, subjectInput, type SubjectDraft } from '../../lib/subjectDraft';
import { verificationNotice } from '../../lib/subjectNames';
import { useApi } from '../../lib/useApi';
import { cn } from '../../lib/utils';
import { SubjectFields } from './SubjectFields';

export function fieldErrors(error: unknown): SubjectFieldError[] {
  if (!isApiError(error) || error.code !== 'VALIDATION_FAILED') return [];
  const fields = (error.details as { fields?: SubjectFieldError[] } | null)?.fields;
  return Array.isArray(fields) ? fields : [];
}

/** The messages of the problems of one field (`subjectForm.errors.<code>`). */
export function useSubjectErrorText(errors: SubjectFieldError[]) {
  const { t } = useTranslation('accounts');
  const tr = t as unknown as (key: string, o?: Record<string, unknown>) => string;
  return (field: string) => {
    const e = errors.filter((x) => x.field === field);
    return e.length ? e.map((x) => tr(`subjectForm.errors.${x.code}`, { defaultValue: x.code })).join(' · ') : null;
  };
}

const selectClass =
  'h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:opacity-50 dark:bg-input/30';

function Labeled({ label, error, className, children }: { label: string; error?: string | null; className?: string; children: ReactNode }) {
  return (
    // eslint-disable-next-line jsx-a11y/label-has-associated-control -- the control is the child
    <label className={cn('grid content-start gap-1.5 text-sm', className)}>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
      {error ? <span className="text-xs text-critical">{error}</span> : null}
    </label>
  );
}

/** Customer picker for a new subject: search by name, pick one. */
function CustomerPicker({ value, onChange, error }: { value: string; onChange: (id: string) => void; error?: string | null }) {
  const { t } = useTranslation('accounts');
  const [q, setQ] = useState('');
  const result = useApi((signal) => api.listCustomers({ q: q.trim() || undefined, limit: 20 }, { signal }), [q]);
  const items = result.status === 'success' ? result.data.items : [];
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      <Labeled label={t('subjectForm.customerSearch')}>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('subjectForm.customerSearchPlaceholder')} />
      </Labeled>
      <Labeled label={t('subjectForm.customer')} error={error}>
        <select className={selectClass} value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">{result.status === 'loading' ? t('subjectForm.loading') : t('subjectForm.choose')}</option>
          {items.map((c) => (
            <option key={c.id} value={c.id}>
              {c.display_name} · {c.email_hint}
            </option>
          ))}
        </select>
      </Labeled>
    </div>
  );
}

function FormBody({ initial, profile, fixedCustomer, onDone, onCancel }: { initial: SubjectDraft; profile?: SubjectProfile; fixedCustomer?: boolean; onDone: (id: string) => void; onCancel: () => void }) {
  const { t } = useTranslation('accounts');
  const editing = Boolean(profile);
  const [d, setD] = useState<SubjectDraft>(initial);
  const set = (patch: Partial<SubjectDraft>) => setD((x) => ({ ...x, ...patch }));
  const write = useWrite((body: SubjectInput, opts) => (profile ? api.newSubjectVersion(profile.id, body, opts) : api.createSubject(body, opts)));
  const [local, setLocal] = useState<SubjectFieldError[]>([]);
  const errors = [...local, ...(write.state.phase === 'error' ? fieldErrors(write.state.error) : [])];
  const err = useSubjectErrorText(errors);
  const kept = Boolean(profile?.identifiers.length);

  const submit = async () => {
    const found = subjectErrors(d, editing, kept);
    if (!editing && !d.customerId) found.push({ field: 'managing_customer_id', code: 'REQUIRED' });
    setLocal(found);
    if (found.length) return;
    const r = await write.run(subjectInput(d, editing, profile?.version));
    if (r) onDone(r.id);
  };

  return (
    <form
      className="grid gap-4"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      {!editing && !fixedCustomer ? <CustomerPicker value={d.customerId} onChange={(customerId) => set({ customerId })} error={err('managing_customer_id')} /> : null}

      <SubjectFields d={d} set={set} err={err} profile={profile} />

      {editing && profile ? (
        <p className="rounded-md border px-3 py-2 text-sm" role="note">
          {t(`subjectForm.${verificationNotice(d, initial)}`)}
        </p>
      ) : null}
      {editing ? (
        <Labeled label={t('subjectForm.reason')} error={err('reason')}>
          <Input value={d.reason} onChange={(e) => set({ reason: e.target.value })} placeholder={t('subjectForm.reasonPlaceholder')} />
        </Labeled>
      ) : null}

      {write.state.phase === 'error' && errors.length === 0 ? <WriteError error={write.state.error} /> : null}
      {errors.length ? <p className="text-sm text-critical">{t('subjectForm.fixFields', { count: errors.length })}</p> : null}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('subjectForm.cancel')}
        </Button>
        <Button type="submit" disabled={write.busy}>
          {editing ? t('subjectForm.saveVersion') : t('subjectForm.create')}
        </Button>
      </DialogFooter>
    </form>
  );
}

function EditBody({ subjectId, onDone, onCancel }: { subjectId: string; onDone: (id: string) => void; onCancel: () => void }) {
  const profile = useApi((signal) => api.getSubjectProfile(subjectId, { signal }), [subjectId]);
  if (profile.status === 'loading') return <Loading rows={4} />;
  if (profile.status === 'error') return <WriteError error={profile.error} />;
  return <FormBody initial={subjectFromProfile(profile.data)} profile={profile.data} onDone={onDone} onCancel={onCancel} />;
}

/** New subject (no `subjectId`) or new version of `subjectId`. `initial`: pre-filled values of a
 * new subject (e.g. a customer's primary subject from the data it had before). */
export function SubjectFormDialog({
  open,
  onOpenChange,
  subjectId,
  customerId = '',
  initial,
  title,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subjectId?: string;
  customerId?: string;
  initial?: Partial<SubjectDraft>;
  title?: string;
  onSaved: (id: string) => void;
}) {
  const { t } = useTranslation('accounts');
  const done = (id: string) => {
    onOpenChange(false);
    onSaved(id);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-3xl [&>*]:min-w-0" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{title ?? (subjectId ? t('subjectForm.editTitle') : t('subjectForm.createTitle'))}</DialogTitle>
          <DialogDescription>{t('subjectForm.description')}</DialogDescription>
        </DialogHeader>
        {open ? (
          subjectId ? (
            <EditBody subjectId={subjectId} onDone={done} onCancel={() => onOpenChange(false)} />
          ) : (
            <FormBody initial={{ ...blankSubject('INDIVIDUAL', customerId), ...initial, customerId }} fixedCustomer={Boolean(customerId)} onDone={done} onCancel={() => onOpenChange(false)} />
          )
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
